// Geolocation utilities for finding nearby charging stations

/**
 * Get user's current location using GPS
 * @returns {Promise<{lat: number, lon: number, accuracy: number}>}
 */
export const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported by this browser'));
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 15000, // 15 seconds - iOS needs more time
      maximumAge: 60000 // 1 minute cache - shorter for iOS
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lon: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
      },
      (error) => {
        let errorMessage = 'Failed to get location';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied by user';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out';
            break;
          default:
            errorMessage = `Unknown location error: ${error.message}`;
            break;
        }
        
        reject(new Error(errorMessage));
      },
      options
    );
  });
};

/**
 * Check if geolocation is supported
 * @returns {boolean}
 */
export const isGeolocationSupported = () => {
  return 'geolocation' in navigator;
};

/**
 * Check geolocation permission status (for supporting browsers)
 * @returns {Promise<string>} 'granted', 'denied', 'prompt', or 'unsupported'
 */
export const checkGeolocationPermission = async () => {
  if (!navigator.permissions) {
    return 'unsupported';
  }
  
  try {
    const permission = await navigator.permissions.query({ name: 'geolocation' });
    return permission.state; // 'granted', 'denied', or 'prompt'
  } catch (error) {
    return 'unsupported';
  }
};

/**
 * Get user's approximate location from IP (fallback)
 * This is a simple implementation - in production you might use a service like ipapi.co
 * @returns {Promise<{lat: number, lon: number, city: string}>}
 */
export const getLocationFromIP = async () => {
  try {
    // For now, return Southern California center as fallback
    // TODO: Implement actual IP geolocation service
    return {
      lat: 33.7,
      lon: -117.8,
      city: 'Southern California',
      accuracy: 50000 // 50km accuracy for IP-based location
    };
  } catch (error) {
    throw new Error('Failed to get location from IP');
  }
};

/**
 * Format distance for display
 * @param {number} distance - Distance in miles
 * @returns {string}
 */
export const formatDistance = (distance) => {
  if (distance < 0.1) {
    return '< 0.1 mi';
  } else if (distance < 1) {
    return `${distance.toFixed(1)} mi`;
  } else {
    return `${Math.round(distance * 10) / 10} mi`;
  }
};

/**
 * Open directions in user's preferred map app
 * @param {number} lat - Destination latitude
 * @param {number} lon - Destination longitude  
 * @param {string} name - Destination name
 */
export const openDirections = (lat, lon, name) => {
  const encodedName = encodeURIComponent(name);
  
  // Detect if user is on iOS or Android for native app integration
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);
  
  if (isIOS) {
    // Try Apple Maps first, fallback to Google Maps
    const appleMapsUrl = `maps://maps.google.com/maps?daddr=${lat},${lon}&amp;ll=`;
    window.open(appleMapsUrl, '_system');
  } else if (isAndroid) {
    // Android users likely prefer Google Maps
    const googleMapsUrl = `https://maps.google.com/maps?daddr=${lat},${lon}`;
    window.open(googleMapsUrl, '_blank');
  } else {
    // Desktop users - open Google Maps in new tab
    const googleMapsUrl = `https://maps.google.com/maps?daddr=${lat},${lon}&amp;ll=`;
    window.open(googleMapsUrl, '_blank');
  }
};